// todo: check if this works

/**
 *
 * @param s String with possible HTML tags
 * @returns Cleaned string
 */
export const cleanupFromTagsGlobal = (s: string) =>
  s
    .replace(/<p>\s*<\/p>/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 *
 * @param s String with possible HTML tags
 * @returns Cleaned string
 */
export const cleanupFromTags = (s: string) =>
  s.replace("<[^>]*>", "");

/**
 *
 * @param s String with possible <script> HTML tags
 * @returns Cleaned string
 */
export const cleanupFromDangerousTags = (s: string) =>
  s.replace(/<\/?script.*\/?>(.*<\/script>)?/gi, "");

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: "\u00a0",
  amp: "&",
  quot: "\"",
  apos: "'",
  lt: "<",
  gt: ">",
  laquo: "«",
  raquo: "»",
  mdash: "—",
  ndash: "–",
  hellip: "…",
};

/**
 * Текст фрагмента HTML для разметки и мета-тегов: без тегов, с раскрытыми сущностями,
 * без лишних пробелов. `<br>` превращается в пробел, чтобы соседние слова не слиплись.
 */
export const htmlToPlainText = (html: string): string =>
  html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) =>
      String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec: string) =>
      String.fromCodePoint(Number(dec)))
    .replace(/&([a-z]+);/gi, (entity: string, name: string) =>
      NAMED_ENTITIES[name.toLowerCase()] ?? entity)
    .replace(/[ \t\r\n]+/g, " ")
    .trim();

/**
 * Ставит неразрывный пробел после слов из одной-двух русских букв (предлоги, союзы),
 * чтобы такое слово не оставалось в конце строки: «консультации с проверенными» → «с\u00a0проверенными».
 * Слово целиком попадает в одно совпадение, поэтому короткие окончания длинных слов не затрагиваются.
 */
export const bindShortWords = (text: string): string =>
  text.replace(/([а-яё]+) /gi, (match: string, word: string) =>
    (word.length <= 2 ? `${word}\u00a0` : match));

/**
 * normalizeBodyHeadings для тела из нескольких частей (textTop → textMiddle → textBottom):
 * части нормализуются как один документ — уровень заголовков части считается от последнего заголовка
 * предыдущей. По отдельности каждая часть начиналась бы с h2, и h3 в начале textBottom становился бы h2.
 * @param htmls части тела в порядке вывода на странице; null/undefined трактуем как пустую строку
 * @returns части с нормализованной иерархией h2..h4, в том же порядке
 */
export const normalizeBodyHeadingsChain = (htmls: (string | null | undefined)[]): string[] => {
  let prevOrig = 0;
  let prevNorm = 1; // виртуальный page-h1 перед телом → первый заголовок тела станет h2
  return htmls.map((html) =>
    (html ?? "").replace(
      /<(h[1-4])((?:\s[^>]*)?)>([\s\S]*?)<\/\1>/gi,
      (_match, tag: string, attrs: string, inner: string) => {
        const orig = Math.max(2, Number(tag[1])); // h1 в теле трактуем как h2
        let norm: number;
        if (orig > prevOrig) norm = prevNorm + 1;
        else if (orig === prevOrig) norm = prevNorm;
        else norm = prevNorm - (prevOrig - orig);
        norm = Math.min(4, Math.max(2, norm));
        prevOrig = orig;
        prevNorm = norm;
        return `<h${norm}${attrs}>${inner}</h${norm}>`;
      },
    ));
};

/**
 * Нормализует иерархию заголовков в теле контента (SEO #10b, task 4510, Фаза 5 «клэмп»).
 * На странице один page-level h1 (из шаблона); тело — под ним, поэтому:
 *  - h1 в теле трактуем как h2 (второй h1 не нужен);
 *  - убираем перескоки уровней: заголовок не может углубиться больше чем на 1 (h2→h4 → h2→h3),
 *    при этом валидные последовательности (h2→h3→h4) сохраняются, атрибуты (в т.ч. id-якоря) — тоже.
 * Уровни зажимаются в диапазон [2..4]. Применять ТОЛЬКО к телу шаблонов с гарантированным page-h1
 * (article/gid/lifestyle/category/goroskop/product-зоны/catalog seoText), не к product/infoPage без page-h1.
 * @param html HTML-строка тела из Strapi (CKEditor); null/undefined трактуем как пустую
 * @returns строка с нормализованной иерархией h2..h4
 */
export const normalizeBodyHeadings = (html?: string | null): string =>
  normalizeBodyHeadingsChain([html])[0];

/**
 * Превращает заголовки h1..h4 в теле карточек/блоков в НЕ-заголовки `<div class="hN">` (SEO #10b).
 * Заголовок карточки — не document heading, поэтому он не должен участвовать в иерархии страницы
 * (иначе даёт перескок вроде h2→h4). Вид сохраняется классом `.hN`. Атрибуты исходного тега — тоже.
 * @param html HTML-строка контента карточки (CKEditor); null/undefined трактуем как пустую
 * @returns строка, где h1..h4 заменены на `<div class="hN">`
 */
export const demoteHeadingsToDiv = (html?: string | null): string =>
  (html ?? "").replace(
    /<(h[1-4])((?:\s[^>]*)?)>([\s\S]*?)<\/\1>/gi,
    (_match, tag: string, attrs: string, inner: string) =>
      `<div class="${tag}"${attrs}>${inner}</div>`,
  );

/**
 * Removes empty paragraphs and list items that contain only whitespace, &nbsp;, or <br> tags
 * @param html HTML string that may contain empty paragraphs and list items
 * @returns Cleaned HTML string
 */
export const removeEmptyParagraphs = (html: string) =>
  html
    .replace(/&nbsp;/gi, "") // Remove all &nbsp;
    .replace(/<p>\s*<\/p>/gi, "") // Remove empty paragraphs
    .replace(/<li>\s*<\/li>/gi, "") // Remove empty list items
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, "") // Remove paragraphs with only <br>
    .replace(/<li>\s*<br\s*\/?>\s*<\/li>/gi, ""); // Remove list items with only <br>

/**
 *
 * @param html String with possible <script> HTML tags
 * @returns Cleaned string
 */
export const fixInvalidImgAttributes = (html: string) =>
  html
    .replace(/width="(\d+)px"/gi, `width="$1"`)
    .replace(/height="(\d+)px"/gi, `height="$1"`)
    .replace(/(srcset="[^"]*?),"/gi, `$1"`);

/**
 *
 * @param userName Our system user name
 * @returns VoxImplant user name
 */
export const convertToViName = (userName: string) =>
  userName.replace("@", "_at_");

/**
 *
 * @param userName VoxImplant user name
 * @returns Our system user name
 */
export const convertToPlatformName = (userName: string) =>
  userName.replace("_at_", "@");

/** Capitalizes the string */
export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

/**
 * trim string and convert letters to lower case
 * @param str
 */
export const trimAndLowerCase = (str: string) =>
  (str ? str.trim().toLowerCase() : str);

/**
 * Converts any phone number to +12345678900 format
 * @param phone Phone in +12345678900, 12345678900, +1 (234) 567-89-00 or any other format
 * @returns
 */
export const convertToPlainPhone = (phone: string) =>
  (phone ? phone.replace(/^(\+)|\D/g, "$1") : phone);

/**
 * Склоняет слово в зависимости от числа
 * @param num Число
 * @param declensions Массив склонений слова вида ['эксперт', 'эксперта', 'экспертов']
 * @returns Слово в правильном склонении
 */
export const declenateWord = (num: number, declensions: string[]) => {
  const lastTwoDigits = num % 100;
  if (lastTwoDigits > 4 && lastTwoDigits < 21) {
    return declensions[2];
  }
  if (num % 10 === 1) {
    return declensions[0];
  }
  if ([2, 3, 4].indexOf(num % 10) !== -1) {
    return declensions[1];
  }
  return declensions[2];
};

export const removeHeadingSlash = (str: string) =>
  str.replace(/^\/*/, "");

export const removeTrailingSlash = (str: string) =>
  str.replace(/\/*\?.*$/, "");

/**
 * Removes slash from the start and from the end of a string
 * @param str
 * @returns
 */
export const trimSlashes = (str: string) =>
  str.replace(/^\/|\/$/g, "");

export const removeTrailingSymbols = (str: string) => {
  const pageMatch = str.match(/\/*\?.*(page=[2-9]|\d\d\d*)&*.*$/);
  return str.replace(/\/*\?.*$/, "") + (pageMatch?.[1] ? `?${pageMatch?.[1]}` : ``);
};

export const getIndexFromUUID = (uuid: number | string, indexRange: number) => {
  let index = 0;
  if (typeof uuid === "number") {
    index = uuid % indexRange;
  } else if (typeof uuid === "string") {
    let sum = 0;
    for (const char of uuid) {
      sum += char.charCodeAt(0);
      index = sum % indexRange;
    }
  }
  return index;
};
